/**
 * Unified Equipment Service
 * 
 * This service provides client-side methods for interacting with the
 * consolidated equipment API.
 */

import api from './api.service';

/**
 * Get equipment with availability information
 * @param {Object} options Search/filter options
 * @param {boolean} [options.includeTrulyAvailableOnly=false] If true, only returns equipment that's physically in inventory and not reserved
 * @param {boolean} [options.includeReserved=true] If true, includes equipment that's reserved for jobs but still in inventory
 */
export const getEquipment = async (options = {}) => {
  try {
    console.log('Equipment service getEquipment called with options:', options);
    
    // Convert options to URL parameters
    const params = new URLSearchParams();

    if (options.search) params.append('search', options.search);
    if (options.category) params.append('category', options.category);
    if (options.subcategory) params.append('subcategory', options.subcategory);
    if (options.status) params.append('status', options.status);
    if (options.jobId) params.append('jobId', options.jobId);
    if (options.siteId) params.append('siteId', options.siteId);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset !== undefined) params.append('offset', options.offset.toString());
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.includeArchived !== undefined) params.append('includeArchived', options.includeArchived.toString());
    
    // Use the equipment-items endpoint to get individual equipment items (not the summary endpoint)
    console.log('Making request to /equipment-items with params:', Object.fromEntries(params));
    const response = await api.get('/equipment-items', { params });
    
    // Check and log response structure to aid debugging
    console.log('Equipment service received response:', 
      response.data ? (response.data.data ? 'Has data array' : 'No data array') : 'No data');
    console.log('Full response structure:', {
      hasData: !!response.data,
      dataKeys: response.data ? Object.keys(response.data) : null,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      dataLength: response.data ? (Array.isArray(response.data) ? response.data.length : 'not array') : 'no data',
      hasSuccess: response.data && response.data.hasOwnProperty('success'),
      hasMeta: response.data && response.data.hasOwnProperty('meta'),
      metaTotal: response.data && response.data.meta ? response.data.meta.total : 'no meta'
    });
    
    // Handle standardized API response format
    let equipment, totalCount, meta;
    
    if (response.data && response.data.success && response.data.data) {
      // Standardized format: { success: true, data: [...], meta: {...} }
      equipment = response.data.data;
      meta = response.data.meta || {};
      totalCount = meta.total || equipment.length;
    } else if (Array.isArray(response.data)) {
      // Legacy format: direct array - server should return paginated response with total count
      equipment = response.data;
      // Without server-side total count, we can't know if there are more items
      // This will show incorrect pagination until server returns proper format
      totalCount = equipment.length;
      console.warn('Server returning direct array without total count - pagination may be incorrect');
      
      meta = {
        total: totalCount,
        page: Math.floor((parseInt(params.get('offset')) || 0) / (parseInt(params.get('limit')) || 50)) + 1,
        limit: parseInt(params.get('limit')) || 50,
        totalPages: Math.ceil(totalCount / (parseInt(params.get('limit')) || 50))
      };
    } else {
      // Fallback
      equipment = [];
      totalCount = 0;
      meta = {};
    }
    
    const limit = parseInt(params.get('limit')) || 50;
    
    return {
      equipment: equipment,
      pagination: {
        totalCount: totalCount,
        page: meta.page || Math.floor((parseInt(params.get('offset')) || 0) / limit) + 1,
        limit: meta.limit || limit,
        pages: meta.totalPages || Math.ceil(totalCount / limit)
      }
    };
  } catch (error) {
    console.error('Error getting equipment:', error);
    // Provide more context in error message for debugging
    const errorMsg = error.response ? 
      `API Error (${error.response.status}): ${error.response.data?.message || error.message}` :
      `Network Error: ${error.message}`;
    
    console.error(errorMsg);
    throw new Error(`Failed to load equipment: ${errorMsg}`);
  }
};

/**
 * Get equipment by ID with full details
 * @param {number|string} id Equipment ID
 * @param {Object} options Query options
 */
export const getEquipmentById = async (id, options = {}) => {
  try {
    // Convert options to URL parameters
    const params = new URLSearchParams();
    
    if (options.includeInventory !== undefined) params.append('includeInventory', options.includeInventory.toString());
    if (options.includeHistory !== undefined) params.append('includeHistory', options.includeHistory.toString());
    
    // Use the V2 equipment endpoint for individual item lookup
    const response = await api.get(`/equipment/${id}`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error getting equipment by ID ${id}:`, error);
    throw error;
  }
};

/**
 * Create new equipment catalog item
 * @param {Object} equipmentData Equipment data
 */
export const createEquipment = async (equipmentData) => {
  try {
    const response = await api.post('/equipment', equipmentData);
    return response.data;
  } catch (error) {
    console.error('Error creating equipment:', error);
    throw error;
  }
};

/**
 * Update equipment catalog item
 * @param {number|string} id Equipment ID
 * @param {Object} updateData Update data
 */
export const updateEquipment = async (id, updateData) => {
  try {
    const response = await api.put(`/equipment/${id}`, updateData);
    return response.data;
  } catch (error) {
    console.error(`Error updating equipment ${id}:`, error);
    throw error;
  }
};

/**
 * Add inventory for equipment
 * @param {number|string} id Equipment ID
 * @param {Object} inventoryData Inventory data
 */
export const addInventory = async (id, inventoryData) => {
  try {
    const response = await api.post(`/equipment/${id}/inventory`, inventoryData);
    return response.data;
  } catch (error) {
    console.error(`Error adding inventory for equipment ${id}:`, error);
    throw error;
  }
};

/**
 * Update inventory status
 * @param {number} inventoryId Inventory ID
 * @param {Object} updateData Update data
 */
export const updateInventoryStatus = async (inventoryId, updateData) => {
  try {
    const response = await api.patch(`/equipment/inventory/${inventoryId}`, updateData);
    return response.data;
  } catch (error) {
    console.error(`Error updating inventory status ${inventoryId}:`, error);
    throw error;
  }
};

/**
 * Plan equipment for job
 * @param {string} jobId Job ID
 * @param {Object} planData Plan data
 */
export const planEquipment = async (jobId, planData) => {
  try {
    const response = await api.post(`/jobs/${jobId}/equipment/plan`, planData);
    return response.data;
  } catch (error) {
    console.error(`Error planning equipment for job ${jobId}:`, error);
    throw error;
  }
};

/**
 * Assign equipment to a job (V2 architecture)
 * @param {string} jobId Job ID to assign equipment to
 * @param {Object} assignData Assignment data
 * @param {string} assignData.equipment_id Equipment ID to assign
 * @param {string} [assignData.site_id] Site ID for assignment (snake_case)
 * @param {string} [assignData.assigned_by] User performing assignment (snake_case)
 * @param {string} [assignData.notes] Assignment notes
 */
export const assignEquipment = async (jobId, assignData) => {
  try {
    const equipmentId = assignData.equipment_id;
    if (!equipmentId) {
      throw new Error('equipment_id is required for assignment');
    }
    
    console.log('Assigning equipment:', { equipmentId, jobId, assignData });
    
    // Clean up equipment ID - remove any invalid characters
    const cleanEquipmentId = String(equipmentId).replace(/[^a-zA-Z0-9\-_]/g, '');
    
    // STEP 1: Check if this is planned or unplanned equipment assignment
    let isPlanned = false;
    try {
      const validationResponse = await api.get(`/equipment-assignment/validate/${cleanEquipmentId}/${jobId}`);
      if (validationResponse.data && validationResponse.data.success) {
        isPlanned = validationResponse.data.data.isPlanned;
        console.log('Equipment assignment validation:', validationResponse.data.data);
      }
    } catch (validationError) {
      console.warn('Could not validate equipment planning, assuming unplanned:', validationError);
      isPlanned = false;
    }
    
    // STEP 2: If unplanned, this method should not proceed directly
    // The calling component should handle the UnplannedEquipmentDialog
    if (!isPlanned && !assignData.forceAssign) {
      const error = new Error('Equipment assignment requires confirmation');
      error.code = 'UNPLANNED_EQUIPMENT';
      error.isPlanned = false;
      error.equipmentId = cleanEquipmentId;
      error.jobId = jobId;
      throw error;
    }
    
    // STEP 3: Proceed with assignment (either planned or user confirmed unplanned)
    const updateData = {
      current_job_id: jobId,
      status: 'assigned',
      notes: assignData.notes || `Assigned to job ${jobId}${assignData.site_id ? ` (Site: ${assignData.site_id})` : ''}`
    };
    
    // Only include updated_by if explicitly provided (V2 uses snake_case)
    if (assignData.assigned_by) {
      updateData.updated_by = assignData.assigned_by;
    }
    
    // Add site assignment if provided (V2 uses snake_case)
    if (assignData.site_id) {
      updateData.current_site_id = assignData.site_id;
    }
    
    console.log('PUT request data:', updateData);
    
    const response = await api.put(`/equipment-items/${cleanEquipmentId}`, updateData);
    
    console.log('V2 Equipment assignment successful:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error assigning equipment to job ${jobId}:`, error);
    console.error('Equipment ID was:', assignData.equipment_id);
    throw error;
  }
};

/**
 * Get job equipment assignments with filtering options
 * @param {string} jobId Job ID
 * @param {Object} options Query options
 */
export const getJobEquipment = async (jobId, options = {}) => {
  try {
    console.log('Getting equipment for job', jobId, 'with options', options);
    
    // Convert options to URL parameters
    const params = new URLSearchParams();
    
    if (options.siteId) params.append('siteId', options.siteId);
    if (options.status) params.append('status', options.status);
    if (options.includeReturned !== undefined) params.append('includeReturned', options.includeReturned.toString());
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    
    const response = await api.get(`/jobs/${jobId}/equipment?${params.toString()}`);
    console.log('Job equipment response:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error getting equipment for job ${jobId}:`, error);
    throw error;
  }
};

/**
 * Update assignment status (install, return, etc.)
 * @param {number} assignmentId Assignment ID
 * @param {Object} updateData Update data
 */
export const updateAssignmentStatus = async (assignmentId, updateData) => {
  try {
    // Use the correct V2 endpoint for updating equipment items
    const response = await api.put(`/equipment-items/${assignmentId}`, updateData);
    return response.data;
  } catch (error) {
    console.error(`Error updating assignment status ${assignmentId}:`, error);
    throw error;
  }
};

/**
 * Get equipment by lifecycle stage
 * Convenience method to filter by specific status
 * @param {string} stage Lifecycle stage ('PLANNED', 'ORDERED', etc.)
 * @param {Object} options Additional filter options
 */
export const getEquipmentByStage = async (stage, options = {}) => {
  try {
    return await getEquipment({
      ...options,
      status: stage
    });
  } catch (error) {
    console.error(`Error getting equipment by stage ${stage}:`, error);
    throw error;
  }
};

/**
 * Update equipment status with history tracking
 * @param {number|string} equipmentId Equipment ID
 * @param {Object} statusData Status update data
 * @param {string} statusData.status New status
 * @param {string} statusData.notes Optional notes
 * @param {string} statusData.timestamp Timestamp of status change
 */
export const updateEquipmentStatus = async (equipmentId, statusData) => {
  try {
    const response = await api.patch(`/equipment/${equipmentId}/status`, statusData);
    return response.data;
  } catch (error) {
    console.error(`Error updating equipment status ${equipmentId}:`, error);
    throw error;
  }
};

/**
 * Get equipment status history
 * @param {number|string} equipmentId Equipment ID
 */
export const getEquipmentStatusHistory = async (equipmentId) => {
  try {
    const response = await api.get(`/equipment-items/${equipmentId}/status-history`);
    console.log('Equipment status history raw response:', response);
    
    // Handle standardized API response format {success: true, data: [...]}
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // Fallback for other response formats
    return response.data || [];
  } catch (error) {
    console.error(`Error getting equipment status history ${equipmentId}:`, error);
    // Return empty array instead of throwing
    return [];
  }
};

/**
 * Bulk update equipment status
 * @param {Array} equipmentIds Array of equipment IDs
 * @param {Object} statusData Status update data
 */
export const bulkUpdateEquipmentStatus = async (equipmentIds, statusData) => {
  try {
    const response = await api.patch('/equipment/bulk-status', {
      equipmentIds,
      ...statusData
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk updating equipment status:', error);
    throw error;
  }
};

/**
 * Get equipment templates (V2)
 * @param {Object} options Query options
 */
export const getTemplates = async (options = {}) => {
  try {
    console.log('equipmentService.getTemplates: Called with options:', options);
    
    const params = new URLSearchParams();
    if (options.search) params.append('search', options.search);
    if (options.category) params.append('category', options.category);
    if (options.isActive !== undefined) params.append('isActive', options.isActive.toString());
    // Default to a reasonable page size if not specified
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.page) params.append('page', options.page.toString());
    if (options.offset !== undefined) params.append('offset', options.offset.toString());
    
    console.log('equipmentService.getTemplates: Making request to /barcode/templates with params:', Object.fromEntries(params));
    
    const response = await api.get('/barcode/templates', { params });
    
    console.log('equipmentService.getTemplates: Response received:', {
      status: response.status,
      dataType: typeof response.data,
      dataKeys: response.data ? Object.keys(response.data) : 'null',
      dataStructure: response.data
    });
    
    // Deep log the response structure to understand the format
    console.log('equipmentService.getTemplates: Full response.data:', JSON.stringify(response.data, null, 2));
    
    return response.data;
  } catch (error) {
    console.error('equipmentService.getTemplates: Error getting equipment templates:', error);
    console.error('equipmentService.getTemplates: Error response:', error.response?.data);
    console.error('equipmentService.getTemplates: Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Create new equipment template (V2)
 * @param {Object} templateData Template data
 */
export const createTemplate = async (templateData) => {
  try {
    console.log('equipmentService.createTemplate: Creating template with data:', templateData);

    // Convert from frontend format to backend format
    // Extract fields from templateData object if present
    const requestData = {
      name: templateData.name,
      barcode_pattern: templateData.barcodePattern || null,
      manufacturer: templateData.templateData?.manufacturer || '',
      model: templateData.templateData?.model || '',
      description: templateData.templateData?.description || '',
      category: templateData.templateData?.category || 'General',
      notes: templateData.templateData?.notes || '',  // Include notes at root level
      template_data: templateData.templateData
    };

    // Use the equipment-templates endpoint for consistency
    const response = await api.post('/equipment-templates', requestData);

    console.log('equipmentService.createTemplate: Template created successfully:', {
      status: response.status,
      data: response.data
    });

    return response.data;
  } catch (error) {
    console.error('equipmentService.createTemplate: Error creating equipment template:', error);
    console.error('equipmentService.createTemplate: Error response:', error.response?.data);
    console.error('equipmentService.createTemplate: Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Update equipment template (V2)
 * @param {number} id Template ID
 * @param {Object} updateData Update data
 */
export const updateTemplate = async (id, updateData) => {
  try {
    console.log('equipmentService.updateTemplate: Updating template with ID:', id, 'and data:', updateData);

    // Convert from frontend format to backend format
    // Extract fields from templateData object if present
    const requestData = {
      name: updateData.name,
      barcode_pattern: updateData.barcodePattern || null,
      manufacturer: updateData.templateData?.manufacturer || '',
      model: updateData.templateData?.model || '',
      description: updateData.templateData?.description || '',
      category: updateData.templateData?.category || 'General',
      notes: updateData.templateData?.notes || '',  // Include notes at root level
      template_data: updateData.templateData
    };

    // Use the proper PUT endpoint for updating templates
    const response = await api.put(`/equipment-templates/${id}`, requestData);

    console.log('equipmentService.updateTemplate: Template updated successfully:', {
      status: response.status,
      data: response.data
    });

    return response.data;
  } catch (error) {
    console.error(`equipmentService.updateTemplate: Error updating equipment template ${id}:`, error);
    console.error('equipmentService.updateTemplate: Error response:', error.response?.data);
    console.error('equipmentService.updateTemplate: Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Delete equipment template (V2)
 * @param {number} id Template ID
 */
export const deleteTemplate = async (id) => {
  try {
    console.log('equipmentService.deleteTemplate: Deleting template with ID:', id);
    
    const response = await api.delete(`/barcode/templates/${id}`);
    
    console.log('equipmentService.deleteTemplate: Template deleted successfully:', {
      status: response.status,
      data: response.data
    });
    
    return response.data;
  } catch (error) {
    console.error(`equipmentService.deleteTemplate: Error deleting equipment template ${id}:`, error);
    console.error('equipmentService.deleteTemplate: Error response:', error.response?.data);
    console.error('equipmentService.deleteTemplate: Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Get equipment categories
 */
export const getCategories = async () => {
  try {
    const response = await api.get('/categories');
    return response.data;
  } catch (error) {
    console.error('Error getting categories:', error);
    throw error;
  }
};

/**
 * Create equipment items (V2 - batch creation)
 * @param {Array} items Array of equipment items to create
 */
export const createEquipmentItems = async (items) => {
  try {
    console.log('Creating equipment items:', items);
    
    // The API expects either { items: [...] } or just an array
    const payload = Array.isArray(items) ? { items } : items;
    
    console.log('Actual payload being sent to API:', JSON.stringify(payload, null, 2));
    
    const response = await api.post('/equipment-items', payload);
    console.log('Equipment items created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating equipment items:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

/**
 * Lookup equipment by barcode (V2 architecture)
 * @param {string} barcode Barcode or equipment ID to lookup
 */
export const lookupByBarcode = async (barcode) => {
  try {
    console.log('Looking up equipment by barcode:', barcode);
    
    const response = await api.get(`/barcode/lookup/${encodeURIComponent(barcode)}`);
    console.log('Equipment lookup successful:', response.data);
    
    // API returns standardized format
    return response.data;
  } catch (error) {
    console.error(`Error looking up equipment by barcode ${barcode}:`, error);
    throw error;
  }
};

/**
 * Return equipment from job (V2 architecture)
 * @param {string} equipment_id Equipment ID to return
 * @param {Object} return_data Return data
 * @param {string} return_data.from_job_id Job ID equipment is being returned from
 * @param {string} return_data.warehouse_id Warehouse to return to
 * @param {string} return_data.zone_id Zone to return to
 * @param {string} [return_data.notes] Optional notes
 * @param {string} [return_data.returned_by] User performing return
 */
export const returnEquipment = async (equipment_id, return_data) => {
  try {
    console.log('Returning equipment:', { equipment_id, return_data });
    
    const response = await api.post(`/equipment-items/${equipment_id}/return`, return_data);
    console.log('Equipment returned successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error returning equipment ${equipment_id}:`, error);
    throw error;
  }
};

/**
 * Transfer equipment between jobs or to sites (V2 architecture)
 * @param {string} equipment_id Equipment ID to transfer
 * @param {Object} transfer_data Transfer data
 * @param {string} [transfer_data.from_job_id] Source job ID (if applicable)
 * @param {string} [transfer_data.to_job_id] Destination job ID
 * @param {string} [transfer_data.to_site_id] Destination site ID
 * @param {string} [transfer_data.notes] Transfer notes
 * @param {string} [transfer_data.transferred_by] User performing transfer
 */
export const transferEquipment = async (equipment_id, transfer_data) => {
  try {
    console.log('Transferring equipment:', { equipment_id, transfer_data });
    
    const response = await api.post(`/equipment-items/${equipment_id}/transfer`, transfer_data);
    console.log('Equipment transferred successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error transferring equipment ${equipment_id}:`, error);
    throw error;
  }
};

/**
 * Search equipment by manufacturer, model, and serial number (fallback lookup)
 * @param {Object} searchParams Search parameters
 * @param {string} searchParams.manufacturer Equipment manufacturer
 * @param {string} searchParams.model Equipment model
 * @param {string} [searchParams.serial_number] Equipment serial number
 */
export const searchEquipment = async (searchParams) => {
  try {
    console.log('Searching equipment by details:', searchParams);
    
    // Convert search parameters to URL parameters
    const params = new URLSearchParams();
    if (searchParams.manufacturer) params.append('manufacturer', searchParams.manufacturer);
    if (searchParams.model) params.append('model', searchParams.model);
    if (searchParams.serial_number) params.append('serial_number', searchParams.serial_number);
    
    const response = await api.get('/equipment-items/search', { params });
    console.log('Equipment search successful:', response.data);
    
    // Return data array directly
    return { data: response.data || [] };
  } catch (error) {
    console.error('Error searching equipment:', error);
    throw error;
  }
};

// Export as object for consistency with other services
/**
 * Bulk archive multiple equipment items
 * @param {Array<number>} equipment_ids Array of equipment IDs to archive
 * @param {string} [reason] Optional reason for archiving
 */
export const bulkArchiveEquipment = async (equipment_ids, reason = null) => {
  try {
    console.log('Bulk archiving equipment:', { equipment_ids, reason });

    const response = await api.post('/equipment-items/bulk-archive', {
      equipment_ids,
      reason
    });

    console.log('Bulk archive completed:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error bulk archiving equipment:', error);
    throw error;
  }
};

const equipmentService = {
  getEquipment,
  getEquipmentById,
  createEquipment,
  updateEquipment,
  addInventory,
  updateInventoryStatus,
  planEquipment,
  assignEquipment,
  getJobEquipment,
  updateAssignmentStatus,
  getEquipmentByStage,
  updateEquipmentStatus,
  getEquipmentStatusHistory,
  bulkUpdateEquipmentStatus,
  // V2 template methods
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getCategories,
  // V2 equipment items
  createEquipmentItems,
  lookupByBarcode,
  returnEquipment,
  transferEquipment,
  searchEquipment,
  bulkArchiveEquipment
};

export default equipmentService;